import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "../../../../../components/ui/input";
import { z } from "zod";
import { Button } from "../../../../../components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../../components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { addTeamMember } from "../../../../../helpers/api";
import { useGetRoles } from "../../../../../helpers/queries/roles/useGetRoles";
import { FiUserPlus } from "react-icons/fi";
import { useState } from "react";

const AddMembersModal = ({setActiveTab}) => {
  const { data: roles } = useGetRoles();
  const [open, setOpen] = useState(false);
  const formSchema = z.object({
    name: z.string().min(1, "Name cannot be empty"),
    email: z.string().min(1, "Email cannot be empty"),
    role: z.string().min(1, "Please select a role"),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
    },
  });

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: addTeamMember,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["team"],
      });
      setActiveTab('invitations')
      toast(data?.message);
      form.reset();
      setOpen(false);
    },
    onError: (error) => {
      toast(error?.response?.data?.message);
    },
  });

  const onSubmit = (data) => {
    mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-fit px-2 rounded-lg">
          <FiUserPlus size={16} />
          <span className="font-medium pr-2">Invite team member</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            Add New Members
          </DialogTitle>
          <DialogDescription>
            Provide the details of the team members you would like to add.
          </DialogDescription>
        </DialogHeader>
        <hr />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-1"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter name" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter recipient email" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} value={field?.value}>
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roles?.roles?.map((role, index) => (
                        <SelectItem key={index} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white rounded-full"
                loading={isPending}
              >
                Submit
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMembersModal;
